import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl } from '@/utils/api-config';
import { apiGet, apiPatch } from '@/utils/api-interceptor';
import { isRecord } from '@/lib/records';
import { SUPPORT_STATUSES, type SupportStatus } from '@/hooks/useSupportIssues';

/** Contact requests share the support workflow's statuses. */
export const CONTACT_STATUSES = SUPPORT_STATUSES;
export type ContactStatus = SupportStatus;

export interface ContactRequest {
  id: number;
  status: ContactStatus;
  admin_notes: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  message: string | null;
  ip_address: string | null;
  status_changed_by: string | null;
  status_changed_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string | null;
}

interface RequestList {
  items: ContactRequest[];
  count: number;
  next: string | null;
  previous: string | null;
}

const KEY = ['contact-requests'];
const ENDPOINT = 'admin/contact-requests/';

function parseRequest(value: unknown): ContactRequest {
  if (!isRecord(value) || typeof value.id !== 'number' || !Number.isSafeInteger(value.id) || value.id <= 0 ||
    typeof value.created_at !== 'string' || !(value.admin_notes == null || typeof value.admin_notes === 'string') ||
    !CONTACT_STATUSES.some((status) => status.value === value.status)) {
    throw new Error('The contact request response contained an invalid request.');
  }
  const status = CONTACT_STATUSES.find((status) => status.value === value.status)!;
  // Form fields are optional for the sender, so anything missing or non-text reads as not provided.
  const text = (key: string) => (typeof value[key] === 'string' && value[key] ? value[key] as string : null);
  return {
    id: value.id, status: status.value, admin_notes: text('admin_notes') ?? '',
    first_name: text('first_name'), last_name: text('last_name'), email: text('email'),
    phone_number: text('phone_number'), message: text('message'), ip_address: text('ip_address'),
    status_changed_by: text('status_changed_by'), status_changed_at: text('status_changed_at'),
    resolved_at: text('resolved_at'), created_at: value.created_at, updated_at: text('updated_at'),
  };
}

async function readResponse(response: Response): Promise<unknown> {
  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    console.error('Could not parse contact request response:', error);
    throw new Error(`Contact request returned an unreadable response (${response.status}).`);
  }
  if (!response.ok) {
    const error = isRecord(body) && isRecord(body.error) ? body.error : body;
    const message = typeof error === 'string' ? error
      : isRecord(body) && typeof body.error === 'string' ? body.error
      : isRecord(error) && typeof error.message === 'string' ? error.message
      : isRecord(body) && typeof body.message === 'string' ? body.message
      : isRecord(body) && typeof body.detail === 'string' ? body.detail
        : `Contact request failed (${response.status}).`;
    const fields = isRecord(error) && isRecord(error.fields) ? error.fields : null;
    const details = fields ? Object.values(fields).flatMap((field) =>
      Array.isArray(field) ? field.filter((text): text is string => typeof text === 'string') : []
    ).join(' ') : '';
    throw new Error(details ? `${message} ${details}` : message);
  }
  // Accept the platform's `data` envelope or a bare DRF body.
  return isRecord(body) && 'data' in body ? body.data : body;
}

export function contactName(request: ContactRequest) {
  return [request.first_name, request.last_name].filter(Boolean).join(' ') || null;
}

export function contactLabel(request: ContactRequest) {
  return contactName(request) || request.email || `Contact request #${request.id}`;
}

export function useContactRequests({ status, search, page }: { status: string; search: string; page: number }) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY, 'list', { status, search, page }],
    enabled: isAuthenticated,
    queryFn: async (): Promise<RequestList> => {
      const query = new URLSearchParams();
      if (status !== 'all') query.set('status', status);
      if (search.trim()) query.set('search', search.trim());
      if (page > 1) query.set('page', String(page));
      const suffix = query.toString();
      const body = await readResponse(await apiGet(getApiUrl(`${ENDPOINT}${suffix ? `?${suffix}` : ''}`)));
      if (Array.isArray(body)) return { items: body.map(parseRequest), count: body.length, next: null, previous: null };
      if (!isRecord(body) || !Array.isArray(body.results) || typeof body.count !== 'number' ||
        !(body.next == null || typeof body.next === 'string') ||
        !(body.previous == null || typeof body.previous === 'string')) {
        throw new Error('The contact requests response did not contain a valid request list.');
      }
      return {
        items: body.results.map(parseRequest), count: body.count,
        next: typeof body.next === 'string' ? body.next : null,
        previous: typeof body.previous === 'string' ? body.previous : null,
      };
    },
  });
}

export function useContactRequest(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: isAuthenticated && !!id,
    queryFn: async () => {
      if (!id || !/^[1-9]\d*$/.test(id)) throw new Error('Invalid contact request ID.');
      return parseRequest(await readResponse(await apiGet(getApiUrl(`${ENDPOINT}${id}/`))));
    },
  });
}

export function useUpdateContactRequest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fields }: { id: number; fields: Partial<Pick<ContactRequest, 'status' | 'admin_notes'>> }) => {
      if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Invalid contact request ID.');
      return parseRequest(await readResponse(await apiPatch(getApiUrl(`${ENDPOINT}${id}/`), fields)));
    },
    onSuccess: (request) => {
      client.setQueryData([...KEY, 'detail', String(request.id)], request);
      return client.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}
